import React, { useEffect, useState } from 'react';
import { Gift, QrCode, Heart, Globe, MapPin, Clock, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { weddingData, images } from '../mockData';

const Gifts = () => {
  const { language } = useLanguage();
  const content = weddingData[language].gifts;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // QR codes internationaux (PayPal, Revolut)
  const internationalQrCodes = [
    {
      name: content.methods[0].name,
      instruction: content.methods[0].instruction,
      image: images.qrPaypal,
      link: 'https://www.paypal.me/Yllenk22'
    },
    {
      name: content.methods[1].name,
      instruction: content.methods[1].instruction,
      image: images.qrRevolut,
      link: 'https://revolut.me/cyrill6db2'
    }
  ];

  // Mobile Money services pour la Côte d'Ivoire
  const ivoireServices = [
    { name: 'Orange Money' },
    { name: 'MTN Mobile Money' },
    { name: 'Wave' }
  ];

  return (
    <div className="min-h-screen pt-16 sm:pt-20 pb-12 sm:pb-20 bg-white">
      {/* Header */}
      <section className="bg-gradient-to-b from-lilac-100 via-lilac-200 to-lilac-100 py-20 sm:py-28 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-48 sm:w-72 h-48 sm:h-72 bg-white/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 sm:w-72 h-48 sm:h-72 bg-gold-200/20 rounded-full blur-3xl" />
        </div>

        <div className={`container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Gift className="mx-auto text-gold-500 w-12 h-12 sm:w-16 sm:h-16 mb-6" />
          <h1 className="font-playfair font-bold text-plum-900 mb-4 sm:mb-6">
            {content.title}
          </h1>
          <div className="divider-gold mt-6 sm:mt-8" />
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="mx-auto text-gold-500 fill-gold-500 w-12 h-12 sm:w-14 sm:h-14 mb-6 animate-pulse" />
            <p className="text-base sm:text-lg lg:text-xl text-plum-700 font-montserrat leading-relaxed px-4">
              {content.intro}
            </p>
            <div className="divider-gold mt-8" />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION INTERNATIONAL
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-white via-lilac-50/30 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Title */}
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl font-playfair font-bold text-plum-900">
              {language === 'fr' ? 'Moyens de dépôts' : 'Deposit Methods'}
            </h2>
          </div>

          {/* International QR Codes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {internationalQrCodes.map((method, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-medium border border-lilac-100 hover:shadow-hover hover:-translate-y-2 transition-all duration-500 group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-5 bg-lilac-50 rounded-full flex items-center justify-center group-hover:bg-gold-50 transition-colors duration-500">
                    <QrCode className="text-lilac-500 group-hover:text-gold-600 w-8 h-8" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-playfair font-bold text-plum-900 mb-2 group-hover:text-gold-600 transition-colors">
                    {method.name}
                  </h3>
                  <p className="text-plum-600 font-montserrat text-sm mb-6">
                    {method.instruction}
                  </p>
                  
                  {/* QR Code */}
                  <div className="bg-lilac-50/50 rounded-xl p-4 mb-4">
                    <img
                      src={method.image}
                      alt={`${method.name} QR Code`}
                      className="w-full max-w-[200px] mx-auto rounded-lg shadow-soft"
                    />
                  </div>
                  
                  <p className="text-gold-600 font-montserrat text-xs font-semibold flex items-center justify-center gap-1">
                    <QrCode size={14} />
                    {language === 'fr' ? 'Scannez le code' : 'Scan the code'}
                  </p>

                  {method.link && (
                    <a
                      href={method.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 bg-gold-500 text-white font-montserrat text-sm font-semibold rounded-full hover:bg-gold-600 transition-colors duration-300"
                      data-testid={`payment-link-${index}`}
                    >
                      <ExternalLink size={14} />
                      {language === 'fr' ? 'Ouvrir le lien' : 'Open link'}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION CÔTE D'IVOIRE — BIENTÔT DISPONIBLE
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-lilac-50/50 via-white to-lilac-50/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Title */}
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl font-playfair font-bold text-plum-900">
              Mobile Money
            </h2>
          </div>

          {/* Coming Soon Card */}
          <div className={`max-w-2xl mx-auto bg-white rounded-2xl sm:rounded-3xl p-8 sm:p-12 shadow-medium border border-gold-100 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-16 h-16 mx-auto mb-6 bg-gold-50 rounded-full flex items-center justify-center">
              <Clock className="text-gold-500 w-8 h-8" />
            </div>
            <h3 className="text-xl sm:text-2xl font-playfair font-bold text-plum-900 mb-4">
              {language === 'fr' ? 'Bientôt disponible' : 'Coming Soon'}
            </h3>
            <p className="text-plum-600 font-montserrat text-sm sm:text-base leading-relaxed mb-8">
              {language === 'fr'
                ? 'Les options de dépôt pour votre soutien seront disponibles bientôt.'
                : 'Gift deposit options will be available soon.'}
            </p>

            {/* Service logos/names */}
            <div className="flex flex-wrap justify-center gap-4">
              {ivoireServices.map((service, index) => (
                <div
                  key={index}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-lilac-50 rounded-full border border-lilac-100"
                >
                  <span className="text-plum-700 font-montserrat text-sm font-medium">{service.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl sm:rounded-3xl p-8 sm:p-12 shadow-medium border border-lilac-100 text-center">
            <Heart className="mx-auto text-gold-500 fill-gold-500 w-10 h-10 sm:w-12 sm:h-12 mb-6" />
            <p className="text-xl sm:text-2xl font-playfair italic text-plum-800 leading-relaxed">
              {content.thankyou}
            </p>
          </div>
        </div>
      </section>

      {/* Note */}
      <section className="py-8 sm:py-12 bg-gradient-to-b from-lilac-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-plum-600 font-montserrat text-sm leading-relaxed italic">
              {language === 'fr'
                ? "Votre présence est le plus précieux des cadeaux. Ces options sont simplement mises à disposition pour ceux qui souhaiteraient contribuer à notre nouveau départ."
                : "Your presence is the most precious gift. These options are simply provided for those who would like to contribute to our new beginning."}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gifts;
